import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import {
  addressesRef,
  getAddressesForUser,
  serverTimestamp,
} from '../../firebase/collections';
import './Dashboard.css';

function Addresses() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getAddressesForUser(user.uid)
      .get()
      .then((snap) => {
        setAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .finally(() => setLoading(false));
  }, [user?.uid]);

  const handleEdit = (address) => {
    setEditingId(address.id);
    setFormData(address);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      label: '',
      fullName: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    try {
      if (editingId) {
        await addressesRef().doc(editingId).update({
          ...formData,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addressesRef().add({
          ...formData,
          userId: user.uid,
          createdAt: serverTimestamp(),
        });
      }
      handleCancel();
      // Reload addresses
      const snap = await getAddressesForUser(user.uid).get();
      setAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error saving address:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('addresses.confirmDelete'))) return;
    try {
      await addressesRef().doc(id).delete();
      setAddresses(addresses.filter((a) => a.id !== id));
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!user) {
    return <p>{t('addresses.pleaseLogIn')}</p>;
  }

  return (
    <div className="dashboardAddresses">
      <div className="dashboardAddressesHeader">
        <h1>{t('addresses.title')}</h1>
        <button
          type="button"
          className="dashboardAddressesAddButton"
          onClick={() => setEditingId('new')}
        >
          {t('addresses.addAddress')}
        </button>
      </div>
      {(editingId || editingId === 'new') && (
        <form className="dashboardAddressesForm" onSubmit={handleSave}>
          <h2>{editingId === 'new' ? t('addresses.addAddress') : t('addresses.editAddress')}</h2>
          <input
            name="label"
            placeholder={t('addresses.label')}
            value={formData.label}
            onChange={handleChange}
            required
          />
          <input
            name="fullName"
            placeholder={t('addresses.fullName')}
            value={formData.fullName}
            onChange={handleChange}
            required
          />
          <input
            name="phone"
            placeholder={t('addresses.phone')}
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <input
            name="addressLine1"
            placeholder={t('addresses.addressLine1')}
            value={formData.addressLine1}
            onChange={handleChange}
            required
          />
          <input
            name="addressLine2"
            placeholder={t('addresses.addressLine2')}
            value={formData.addressLine2}
            onChange={handleChange}
          />
          <input
            name="city"
            placeholder={t('addresses.city')}
            value={formData.city}
            onChange={handleChange}
            required
          />
          <input
            name="state"
            placeholder={t('addresses.state')}
            value={formData.state}
            onChange={handleChange}
          />
          <input
            name="zipCode"
            placeholder={t('addresses.zipCode')}
            value={formData.zipCode}
            onChange={handleChange}
            required
          />
          <input
            name="country"
            placeholder={t('addresses.country')}
            value={formData.country}
            onChange={handleChange}
            required
          />
          <div className="dashboardAddressesFormActions">
            <button type="submit">{t('common.save')}</button>
            <button type="button" onClick={handleCancel}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p>{t('common.loading')}</p>
      ) : addresses.length === 0 ? (
        <div className="emptyState">
          <p>{t('addresses.noSavedAddresses')}</p>
        </div>
      ) : (
        <ul className="dashboardAddressesList">
          {addresses.map((address) => (
            <li key={address.id} className="dashboardAddressesItem">
              <div className="dashboardAddressesItemContent">
                <h3>{address.label}</h3>
                <p>{address.fullName}</p>
                <p>{address.phone}</p>
                <p>
                  {address.addressLine1}
                  {address.addressLine2 && `, ${address.addressLine2}`}
                </p>
                <p>
                  {address.city}, {address.state} {address.zipCode}
                </p>
                <p>{address.country}</p>
              </div>
              <div className="dashboardAddressesItemActions">
                <button type="button" onClick={() => handleEdit(address)}>
                  {t('common.edit')}
                </button>
                <button type="button" onClick={() => handleDelete(address.id)}>
                  {t('common.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Addresses;